import {
  IonContent,
  IonIcon,
  IonPage,
  useIonViewDidEnter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCard,
  Container,
  decOfNum,
  priceNormalize,
  ProfileInformation,
  ReviewSwiperSection,
  SpecialistMatch,
  SpecialistStatistic,
  SupportedCurrency,
  Typography,
  TypographyVariantsEnum,
} from '@mindly/ui-components';
import { ISpecialist, StripeSupportedCurrency, SupportedCurrencies } from '../../redux/specialists/types';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router';
import { setActiveTab, setIsActive, Tabs } from '../../redux/tabBar/reducer';
import { userSelector } from '../../redux/user/selector';
import { numeralDeclension } from '../../utils';
import { correctPriceType, cutTo2Decimals, delay } from '../PaymentPage/utils';
import StripeCardElement from './StripeCardElement/StripeCardElement';
import { SecureInfo } from '../PaymentPage/SecureInfo/SecureInfo';
import LoadPaymentModal from '../../modals/LoadPaymentModal/LoadPaymentModal';
import { PrivateRoutes, PublicRoutes } from '../../routes/navigation';
import { loadSavedCreditCards } from '../../redux/savedCreditCards/action';
import { fetchSubscriptions } from '../../redux/subscriptions/thunk';
import { subscriptionsInfoSelector } from '../../redux/subscriptionsInfo/selector';
import { fetchSubscriptionsInfo } from '../../redux/subscriptionsInfo/thunk';
import { SubscriptionsInfo } from '../../redux/subscriptionsInfo/types';
import { selectRoomDataBySenderId } from '../../redux/chatRooms/selector';
import { fetchChatRoomsBySpecialistID } from '../../redux/chatRooms/thunk';
import useAgoraRtm from '../../context/AgoraContext/AgoraContext';
import { SystemMessagesType, UserType } from '../types';
import { getUsersPsychologistsListThunk } from '../../redux/usersPsychologists/action';
import { createSubscription, CreateSubscriptionReturnType } from '../../api/subscription';
import { getFirestoreGlobalVariablesThunk } from '../../redux/firestoreGlobalVariables/action';
import { FormattedMessage, useIntl } from 'react-intl';
import useLangs from '../../context/LangsContext';
import { formatSystemMessage } from '../../context/AgoraContext/formatSystemMessage';
import SubscriptionsPaymentSkeleton from './SubscriptionsPaymentSkeleton';
import EmailSystem from '../../api/emails';
import PageHeader from '../../components/PageHeader/PageHeader';
import { globalVariables } from '../../redux/firestoreGlobalVariables/selector';
import { specialistReviewsSelector } from '../../redux/specialistReviews/selector';
import { fetchReviews } from '../../redux/specialistReviews/thunk';
import {
  PaymentQuestionsButton,
  PaymentRow,
  PaymentSubscriptionInfo,
  PaymentSubscriptionWrapper,
} from './SubscriptionsPayment.style';
import { informationCircle } from 'ionicons/icons';
import SubscriptionAdvantages from '../../components/SubscriptionAdvantages';
import { FAQ_PLAN_THERAPY, SPECIALIST_MATCH, SPECIALIST_STATISTIC, WHAT_PLAN_THERAPY } from '../../intercom-articles';
import { Capacitor } from '@capacitor/core';
import useToast from '../../context/ToastContext/ToastContext';
import { useTranslation } from 'react-i18next';
import { CouponPreviewDetailsType, useAnalytics } from '../../shared';
import { fetchSpecialistByID } from '../../redux/loadedSpecialists/thunk';
import DiscountInputFeature from '../../features/DiscountInputFeature/DiscountInputFeature';
import { applyCouponToSubscription, clearCouponFromSubscription } from '../../api/coupons';
import { specialistSelectorPositionById } from '../../redux/specialists/selector';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { environment } from 'src/enviroments';
import useIntercomPlugin from 'src/shared/hooks/useIntercomPlugin';

const stripe = loadStripe(environment.STRIPE_PUBLIC_KEY);

interface PaymentPageProps
  extends RouteComponentProps<{
    id: string;
    monthCount?: string;
    selectedPlan?: string;
  }> {}

const SubscriptionsPayment: React.FC<PaymentPageProps> = ({ match }) => {
  const { id, monthCount, selectedPlan } = match.params;

  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const { t } = useTranslation(['subscriptions', 'gift-certificates']);

  const { showToast } = useToast();
  const { locale } = useLangs();
  const { logEvent, displayArticle } = useIntercomPlugin();
  const { trackPredefinedEvent } = useAnalytics();

  const [isLoading, setIsLoading] = useState(false);
  const [retryNumber, setRetryNumber] = useState<number>(0);
  const [subscriptionID, setSubscriptionID] = useState<string | null>(null);
  const [isSendMessage, setIsSendMessage] = useState(false);
  const [isEventTracked, setIsEventTracked] = useState(false);
  const [isPageView, setIsPageView] = useState(false);

  const [paymentStatus, setPaymentStatus] = useState<null | 'confirmed' | 'paid' | 'unknown'>(null);
  const [fetchingStatus, setFetchingStatus] = useState<null | 'start' | 'success'>(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState<CreateSubscriptionReturnType | null>(null);

  const modalRef = useRef<HTMLIonModalElement | null>(null);

  const { sendSystemMessage } = useAgoraRtm();

  const [specialist, setSpecialist] = useState<ISpecialist | null>(null);

  const [couponPreviewDetails, setCouponPreviewDetails] = useState<CouponPreviewDetailsType | null>(null);
  const [couponPreviewFailureMessage, setCouponPreviewFailureMessage] = useState('');
  const [isCouponExpanded, setIsCouponExpanded] = useState<boolean | null>(null);
  const [isCouponLoading, setIsCouponLoading] = useState<boolean>(false);
  const [isDetachCouponLoading, setIsDetachCouponLoading] = useState<boolean>(false);

  const user = useSelector(userSelector);
  const roomData = useSelector(selectRoomDataBySenderId(id));
  const { data: subscriptionsInfo } = useSelector(subscriptionsInfoSelector());
  const globalVars = useSelector(globalVariables);
  const reviews = useSelector(specialistReviewsSelector(specialist?.id ?? ''));
  const specialistPosition = useSelector(specialistSelectorPositionById(specialist?.id ?? ''));

  const sessionsCount = useMemo(() => {
    if (!selectedPlan || !monthCount) return null;
    return +selectedPlan * (+monthCount * 4);
  }, [monthCount, selectedPlan]);

  const sessionQuantity = useMemo(() => {
    if (!sessionsCount) return null;
    return `${sessionsCount} ${numeralDeclension(sessionsCount, [
      intl.formatMessage({
        id: 'other.sessions.one',
        defaultMessage: 'сеанс',
      }),
      intl.formatMessage({
        id: 'other.sessions.many',
        defaultMessage: 'сеанси',
      }),
      intl.formatMessage({
        id: 'other.sessions.several',
        defaultMessage: 'сеансів',
      }),
    ])}`;
  }, [sessionsCount, intl]);

  const monthInterval = useMemo(() => {
    if (!monthCount) return null;
    return `${+monthCount === 1 ? '' : `${monthCount} `}${numeralDeclension(+monthCount, [
      intl.formatMessage({
        id: 'other.month.one',
        defaultMessage: 'місяць',
      }),
      intl.formatMessage({
        id: 'other.month.many',
        defaultMessage: 'місяці',
      }),
      intl.formatMessage({
        id: 'other.month.several',
        defaultMessage: 'місяців',
      }),
    ])}`;
  }, [monthCount, intl]);

  const validateParams = useCallback(
    (localSubscriptionsInfo: SubscriptionsInfo) => {
      if (!selectedPlan || !monthCount) return;
      if (
        !localSubscriptionsInfo.possible_number_of_sessions_per_week.find((el) => el.value === +selectedPlan) ||
        !localSubscriptionsInfo.possible_intervals_of_months.includes(+monthCount)
      ) {
        showToast({
          text: 'Схоже було обрано невірні параметри підписки, будь-ласка спробуйте ще раз, або звяжіться з тех.підтримкою',
        });
        history.go(-1);
      }
    },
    [selectedPlan, monthCount]
  );

  useIonViewWillEnter(() => {
    setIsEventTracked(false);
    setIsPageView(true);

    logEvent('Enter subscription payment page');
  }, []);

  useIonViewDidEnter(async () => {
    if (!user?.id) return history.push(PublicRoutes.AUTH);

    try {
      if (!user?.id) return;

      setIsLoading(true);
      dispatch(setIsActive(false));

      const localSpecialist = await dispatch(fetchSpecialistByID(id)).unwrap();
      if (!localSpecialist) throw new Error('no_such_therapist');
      setSpecialist(localSpecialist);

      await dispatch(loadSavedCreditCards(user?.id));
      await dispatch(fetchReviews({ specialistID: id }));
      const localSubscriptionsInfo = await dispatch(
        fetchSubscriptionsInfo({
          userID: user?.id,
          therapistID: specialist?.id,
        })
      ).unwrap();

      await dispatch(getFirestoreGlobalVariablesThunk());
      validateParams(localSubscriptionsInfo);

      if (!selectedPlan) throw new Error('session_plan_not_available');
      if (!monthCount) throw new Error('subscription_months_unavailable');

      const createSubscriptionData = await createSubscription({
        userID: user?.id,
        frontendPriceInUAH: localSpecialist.session_price,
        sessionPerWeek: selectedPlan,
        therapistID: localSpecialist?.id,
        monthInterval: monthCount,
      });
      setSubscriptionInfo(createSubscriptionData);
      setCouponPreviewDetails(createSubscriptionData.couponPreview);

      setIsLoading(false);
    } catch (error: any) {
      console.error(error);
      showToast({ error });
      history.push(PrivateRoutes.PSYCHOLOGIST_LIST);
      dispatch(setIsActive(true));
      dispatch(setActiveTab(Tabs.second));
    } finally {
      setIsLoading(false);
    }
  }, [id, monthCount, selectedPlan, user?.id, specialist?.id, locale]);

  useIonViewWillLeave(() => {
    setIsPageView(false);
    setIsLoading(false);
    setPaymentStatus(null);
    setFetchingStatus(null);
    setRetryNumber(0);
    setSubscriptionInfo(null);
    setSubscriptionID(null);
    setCouponPreviewDetails(null);
    setCouponPreviewFailureMessage('');
    setIsCouponExpanded(null);
    setIsCouponLoading(false);
  }, []);

  useEffect(() => {
    (async () => {
      if (paymentStatus !== 'confirmed' || retryNumber > 10 || !user?.id || !subscriptionID) return;

      await delay(500);

      const subscriptionsResult = await dispatch(fetchSubscriptions(user.id)).unwrap();
      const subscription = subscriptionsResult?.find((el) => el.stripe_subscription_id === subscriptionID);
      await dispatch(getUsersPsychologistsListThunk());

      if (!!subscription && fetchingStatus !== 'success') {
        setPaymentStatus('paid');
        setFetchingStatus('success');

        const subscriptionPrice = StripeSupportedCurrency.includes(subscription.session_price_type)
          ? subscription.session_price * (subscription?.session_count ?? 0)
          : subscription.session_price_default * (subscription?.session_count ?? 0);

        trackPredefinedEvent('Subscribe', {
          contents: [{ content_id: id, content_type: 'product' }],
          eventInstanceId: subscription.id,
          currency: correctPriceType(subscription.session_price_type) as unknown as SupportedCurrency,
          value: subscriptionPrice,
          userId: user?.id,
          ...(user?.email && { email: user?.email }),
          ...(user?.display_name && { firstName: user?.display_name }),
          ...(user?.last_name && { lastName: user?.last_name }),
        });
      }

      if (retryNumber >= 10 && fetchingStatus !== 'success') {
        setPaymentStatus('unknown');
        setFetchingStatus('success');
      }

      setRetryNumber(retryNumber + 1);
    })();
  }, [paymentStatus, retryNumber, user, subscriptionID, fetchingStatus, roomData]);

  useEffect(() => {
    const fetchRoomData = async () => {
      if (!roomData?.room.id) {
        await dispatch(
          fetchChatRoomsBySpecialistID({
            clientID: user?.id ?? '',
            specialistId: id,
          })
        );
      }
    };

    fetchRoomData();
  }, [roomData?.room.id, user?.id, id]);

  useEffect(() => {
    if (fetchingStatus === 'success' && !isSendMessage) {
      user?.id &&
        sendSystemMessage({
          user: user as Omit<UserType, 'id'> & { id: string },
          specialistId: id,
          message: formatSystemMessage(
            SystemMessagesType.subscription_create,
            {
              therapistName: `${specialist?.first_name ?? ''} ${specialist?.last_name?.[0] ?? ''}`,
              sessionQuantity: sessionQuantity ?? '',
              monthInterval: monthInterval ?? '',
            },
            intl
          ),
          notifyMessage: intl.formatMessage(
            {
              id: 'subscription.notify_message.create',
              defaultMessage: 'Оформив(ла) підписку на {monthInterval} на {sessionQuantity}',
            },
            { sessionQuantity, monthInterval }
          ),
          roomData,
          locale,
        });

      subscriptionInfo && EmailSystem.sendSubscription('created', subscriptionInfo.firestore_subscription_id);

      const analyticsParams = {
        currency: StripeSupportedCurrency.includes(subscriptionInfo?.price_type ?? SupportedCurrencies.UAH)
          ? subscriptionInfo?.price_type
          : SupportedCurrencies.EUR,
        value: subscriptionInfo?.amount ? subscriptionInfo.amount / 100 : 0,
        specialist_name: `${specialist?.first_name} ${specialist?.last_name}`,
        specialist_id: specialist?.id,
        platform: Capacitor.getPlatform(),
      };

      trackPredefinedEvent('purchase', analyticsParams);
      trackPredefinedEvent('purchase_subscription', analyticsParams);
      setIsSendMessage(true);
    }
  }, [
    isSendMessage,
    fetchingStatus,
    user,
    specialist,
    sessionQuantity,
    monthInterval,
    sessionsCount,
    selectedPlan,
    monthCount,
    subscriptionsInfo,
    subscriptionInfo,
    cutTo2Decimals,
  ]);

  useEffect(() => {
    if (!isPageView) return;
    if (!specialist) return;
    if (isEventTracked) return;
    if (!monthCount) return;
    if (!user) return;

    setIsEventTracked(true);
    trackPredefinedEvent('SUBSCRIPTION_CHECKOUT_SCREEN_VIEWED', {
      specialist_id: specialist.id,
      specialist_name: `${specialist.first_name} ${specialist.last_name}`,
      subscription_length: +monthCount,
      specialist_order_position: specialistPosition,
    });
  }, [specialist, isPageView, isEventTracked, monthCount, user, specialistPosition]);

  const showAllReviews = useCallback(() => {
    history.push(PrivateRoutes.SPECIALIST_REVIEWS.replace(':id', specialist?.id ?? ''));
  }, [specialist?.id]);

  const onCouponSubmitHandler = useCallback(
    async (coupon: string) => {
      try {
        if (!subscriptionInfo?.subscription_id) return;
        setIsCouponLoading(true);
        const { couponPreview, ...response } = await applyCouponToSubscription(
          subscriptionInfo?.subscription_id,
          coupon.trim()
        );

        setSubscriptionInfo(response);
        setCouponPreviewDetails(couponPreview);
      } catch (error: any) {
        const errors = error.error.map((el: any) => el.msg);
        setCouponPreviewFailureMessage(errors?.length > 1 ? errors.join(', ') : errors[0]);
      } finally {
        setIsCouponLoading(false);
      }
    },
    [couponPreviewDetails]
  );

  const onCouponCancelHandler = useCallback(async () => {
    try {
      if (!subscriptionInfo?.subscription_id || !couponPreviewDetails?.coupon_code) {
        return;
      }

      setIsDetachCouponLoading(true);

      const cancelSubscriptionData = await clearCouponFromSubscription(
        subscriptionInfo?.subscription_id,
        couponPreviewDetails?.coupon_code
      );
      setSubscriptionInfo(cancelSubscriptionData);

      setCouponPreviewDetails(null);
      setIsCouponExpanded(false);
      setIsCouponLoading(false);
      setCouponPreviewFailureMessage('');
    } catch (error) {
      showToast({ error });
    } finally {
      setIsDetachCouponLoading(false);
    }
  }, [subscriptionInfo?.subscription_id, couponPreviewDetails?.coupon_code]);

  const originalAmount = useMemo(() => {
    if (!subscriptionInfo?.amount) return 0;

    if (couponPreviewDetails?.payment_amount_original) {
      return couponPreviewDetails.payment_amount_original / 100;
    }

    return subscriptionInfo.amount / 100;
  }, [subscriptionInfo?.amount, couponPreviewDetails?.payment_amount_original]);

  return (
    <IonPage>
      <PageHeader
        backChevron={true}
        intercomId={FAQ_PLAN_THERAPY}
        title={intl.formatMessage({
          id: 'bocking.payment.title',
          defaultMessage: 'Оплата',
        })}
        subtitle={intl.formatMessage(
          { id: 'bocking.step', defaultMessage: 'Крок {from} із {to}' },
          {
            from: 2,
            to: 2,
          }
        )}
      />
      <LoadPaymentModal
        ref={modalRef}
        fetchingStatus={fetchingStatus}
        paymentStatus={paymentStatus}
        subscriptionData={{ subscriptionID: subscriptionID, therapistID: id }}
        loadType={'subscription'}
      />
      <IonContent>
        <Container>
          {isLoading ? (
            <SubscriptionsPaymentSkeleton />
          ) : (
            <PaymentSubscriptionWrapper>
              <ProfileInformation
                style={{ padding: 0, marginBottom: '34px' }}
                isLoading={isLoading}
                specialistAvatar={specialist?.avatar_link as string}
                specialistFirstName={specialist?.first_name}
                specialistLastName={specialist?.last_name}
                specialistSpecialization={specialist?.specialization as string}
                countryCode={specialist?.country_of_birthday as string}
                countOfReviews={
                  (specialist?.reviews_count ?? 0) >= globalVars.specialistReviewsCountForShowing
                    ? specialist?.reviews_count
                    : undefined
                }
                averageStarsCount={
                  (specialist?.reviews_count ?? 0) >= globalVars.specialistReviewsCountForShowing
                    ? specialist?.reviews_average_rating
                    : undefined
                }
                translations={{
                  reviews: [
                    intl.formatMessage({
                      id: 'other.specialistReviews.one',
                      defaultMessage: 'відгук',
                    }),
                    intl.formatMessage({
                      id: 'other.specialistReviews.many',
                      defaultMessage: 'відгуки',
                    }),
                    intl.formatMessage({
                      id: 'other.specialistReviews.several',
                      defaultMessage: 'відгуків',
                    }),
                  ],
                }}
              />

              {sessionQuantity && monthInterval && monthCount && subscriptionInfo && (
                <PaymentSubscriptionInfo>
                  <div className={'flex flex-col gap-1'}>
                    <div className={'info-item'}>
                      <div className={'info-title'}>{sessionQuantity}</div>
                      <div className={'info-title'}>
                        {priceNormalize({
                          price: originalAmount,
                          currency: correctPriceType(subscriptionInfo.price_type) as unknown as SupportedCurrency,
                          extendedUsd: true,
                        })}
                      </div>
                    </div>
                    {subscriptionInfo?.subscription_price_type === 'UAH' && (
                      <div className={'flex items-center justify-end w-full'}>
                        <Typography variant={TypographyVariantsEnum.Text14Regular} opacity={50}>
                          <FormattedMessage id={'bocking.in.uan'} defaultMessage={'Приблизно'} />{' '}
                          {priceNormalize({
                            price: subscriptionInfo.price_local,
                            currency: SupportedCurrency.UAH,
                          })}
                        </Typography>
                      </div>
                    )}
                  </div>

                  <div>
                    <DiscountInputFeature
                      onCancel={onCouponCancelHandler}
                      onSubmit={onCouponSubmitHandler}
                      setIsExpanded={setIsCouponExpanded}
                      isExpanded={isCouponExpanded}
                      isLoading={isCouponLoading}
                      failureMessage={couponPreviewFailureMessage}
                      couponPreviewDetails={couponPreviewDetails}
                      showLoader={isDetachCouponLoading}
                      type="coupon"
                    />
                  </div>

                  {couponPreviewDetails && (
                    <div>
                      <div className={'flex items-center justify-between'}>
                        <Typography variant={TypographyVariantsEnum.Text18Semi}>
                          <FormattedMessage id={'booking.payment.pay'} defaultMessage={'До сплати'} />
                        </Typography>
                        <div className={'flex items-center gap-2'}>
                          <Typography variant={TypographyVariantsEnum.Text18Regular} className={'line-through'}>
                            {priceNormalize({
                              price: originalAmount,
                              currency: correctPriceType(subscriptionInfo.price_type) as unknown as SupportedCurrency,
                              extendedUsd: true,
                            })}
                          </Typography>
                          <Typography variant={TypographyVariantsEnum.Text18Semi}>
                            {priceNormalize({
                              price: couponPreviewDetails.payment_amount_final / 100,
                              currency: correctPriceType(subscriptionInfo.price_type) as unknown as SupportedCurrency,
                              extendedUsd: true,
                            })}
                          </Typography>
                        </div>
                      </div>
                      {!StripeSupportedCurrency.includes(
                        (subscriptionInfo?.subscription_price_type ??
                          SupportedCurrencies.EUR) as unknown as SupportedCurrencies
                      ) && (
                        <Typography
                          variant={TypographyVariantsEnum.Text14Regular}
                          opacity={50}
                          className={'flex justify-end'}
                        >
                          <FormattedMessage id={'bocking.in.uan'} defaultMessage={'Приблизно'} />{' '}
                          {priceNormalize({
                            price: couponPreviewDetails.payment_amount_local / 100,
                            currency: SupportedCurrency.UAH,
                          })}
                        </Typography>
                      )}
                    </div>
                  )}

                  <div>
                    <Typography variant={TypographyVariantsEnum.Text14Regular}>
                      {t('subscriptions.general.subscriptionUpdates')}{' '}
                      {+monthCount === 1 ? (
                        <FormattedMessage id={'general.every.one'} defaultMessage={'кожен'} />
                      ) : (
                        <FormattedMessage id={'general.every'} defaultMessage={'кожні'} />
                      )}{' '}
                      {monthInterval}
                    </Typography>
                    <button
                      className={'info-help'}
                      onClick={async () => {
                        await displayArticle(WHAT_PLAN_THERAPY);
                      }}
                    >
                      <Typography>
                        <FormattedMessage
                          id={'subscription.what.that'}
                          defaultMessage={'Дізнатися що таке підписка на терапію'}
                        />
                      </Typography>
                      <IonIcon icon={informationCircle} />
                    </button>
                  </div>
                </PaymentSubscriptionInfo>
              )}

              {couponPreviewDetails?.payment_amount_final === 0 && (
                <div className={'pb-5'}>
                  <AlertCard
                    text={t('gift-certificates:checkout.gift_card.full_cover_notice')}
                    icon={'☝️'}
                    variant={'blue'}
                    size={'XY4'}
                  />
                </div>
              )}

              {user?.id &&
                specialist?.id &&
                selectedPlan &&
                monthCount &&
                subscriptionInfo &&
                (couponPreviewDetails?.payment_amount_charged ?? subscriptionInfo.amount) > 0 && (
                  <Elements
                    stripe={stripe}
                    options={{
                      clientSecret: subscriptionInfo.client_secret,
                    }}
                  >
                    <StripeCardElement
                      specialist={specialist}
                      sessionPrice={(subscriptionInfo?.amount / 100).toString()}
                      setPaymentStatus={setPaymentStatus}
                      setFetchingStatus={setFetchingStatus}
                      setSubscriptionID={setSubscriptionID}
                      subscriptionInfo={subscriptionInfo}
                      couponPreviewDetails={couponPreviewDetails}
                      disabled={isCouponLoading || isDetachCouponLoading}
                    />
                  </Elements>
                )}
              <PaymentRow style={{ paddingTop: 0 }}>
                <SubscriptionAdvantages />
              </PaymentRow>
              {specialist?.percent_match && (
                <PaymentRow>
                  <SpecialistMatch
                    className={'specialist-match'}
                    learnMoreClick={async () => {
                      await displayArticle(SPECIALIST_MATCH);
                    }}
                    isLoading={isLoading}
                    percentMatch={specialist.percent_match}
                    specialistFirstName={specialist?.first_name as string}
                    translations={{
                      matchYou: intl.formatMessage({
                        id: 'specialist.profile.matchYou',
                        defaultMessage: 'підходить Вам на',
                      }),
                      howWeMatchThis: intl.formatMessage({
                        id: 'specialist.profile.howWeMatchThis',
                        defaultMessage: 'Дізнатися як ми це прогнозуємо',
                      }),
                    }}
                  />
                </PaymentRow>
              )}
              {(specialist?.super_specialist || !!specialist?.session_count_by_3_days) && (
                <PaymentRow>
                  <SpecialistStatistic
                    className={'specialist-stats'}
                    trendingUpIcon={'./trendingUpOutline.svg'}
                    superSpecialistIcon={'./flashOutline.svg'}
                    learnMoreClick={async () => {
                      await displayArticle(SPECIALIST_STATISTIC);
                    }}
                    specialistFirstName={specialist?.first_name}
                    specialistLastName={specialist?.last_name}
                    isSuperSpecialist={!!specialist?.super_specialist}
                    countConsultationFor3Days={specialist?.session_count_by_3_days ?? 0}
                    isLoading={isLoading}
                    translations={{
                      bookedSession: intl.formatMessage({
                        id: 'specialist.profile.bookedSession',
                        defaultMessage: 'заброньовано за останні 72 години',
                      }),
                      superSpecialistInfo: intl.formatMessage(
                        {
                          id: 'specialist.profile.superSpecialistInfo',
                          defaultMessage: `високорейтинговий і надійний спеціаліст.{space}`,
                        },
                        { space: ' ' }
                      ),
                      knowMore: intl.formatMessage({
                        id: 'specialist.profile.knowMore',
                        defaultMessage: `Дізнатися більше`,
                      }),
                      superSpecialist: intl.formatMessage({
                        id: 'specialist.profile.superSpecialist',
                        defaultMessage: 'Супер спеціаліст',
                      }),
                      popularSpecialist: intl.formatMessage({
                        id: 'specialist.profile.popularSpecialist',
                        defaultMessage: 'Популярний спеціаліст',
                      }),
                      sessions: [
                        intl.formatMessage({
                          id: 'other.sessions.one',
                          defaultMessage: 'сеанс',
                        }),
                        intl.formatMessage({
                          id: 'other.sessions.many',
                          defaultMessage: 'сеанси',
                        }),
                        intl.formatMessage({
                          id: 'other.sessions.several',
                          defaultMessage: 'сеансів',
                        }),
                      ],
                    }}
                  />
                </PaymentRow>
              )}
              {!!reviews?.length && (
                <PaymentRow className={'review-section'}>
                  <ReviewSwiperSection
                    loading={isLoading}
                    reviews={reviews ?? []}
                    reviewsCount={specialist?.reviews_count ?? 0}
                    averageRating={specialist?.reviews_average_rating ?? 0}
                    showAllReviewsHandler={showAllReviews}
                    specialistReviewsCountForShowing={globalVars.specialistReviewsCountForShowing}
                    translations={{
                      showMore: intl.formatMessage({
                        id: 'general.showMore.btn',
                        defaultMessage: 'Показати більше',
                      }),
                      showAllReviews: intl.formatMessage({
                        id: 'specialistProfile.reviews.showAll',
                        defaultMessage: 'Показати всі відгуки',
                      }),
                      reviews: decOfNum(specialist?.reviews_count ?? 0, [
                        intl.formatMessage({
                          id: 'other.specialistReviews.one',
                          defaultMessage: 'відгук',
                        }),
                        intl.formatMessage({
                          id: 'other.specialistReviews.many',
                          defaultMessage: 'відгуки',
                        }),
                        intl.formatMessage({
                          id: 'other.specialistReviews.several',
                          defaultMessage: 'відгуків',
                        }),
                      ]),
                    }}
                  />
                </PaymentRow>
              )}
              <PaymentQuestionsButton
                onClick={async () => {
                  await displayArticle(FAQ_PLAN_THERAPY);
                }}
                style={{ marginTop: '20px' }}
              >
                <IonIcon icon={informationCircle} />{' '}
                <FormattedMessage id={'bocking.faq'} defaultMessage={'Питання що часто задаються'} />
              </PaymentQuestionsButton>
              <SecureInfo order={'text-last'} />
            </PaymentSubscriptionWrapper>
          )}
        </Container>
      </IonContent>
    </IonPage>
  );
};

export default SubscriptionsPayment;
